import React, { useMemo, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { DateTime } from 'luxon';
import { useFormContext } from 'react-hook-form';
import Switch from 'react-switch';

import FormItem from "app/components/inputs/FormItem";
import TimeRangeInput from "app/components/inputs/TimeRangeInput";
import DateSelector from "./DateSelector";
import RecurrenceSelector from "./RecurrenceSelector";
import { UserContext, RoleType } from 'app/contexts/UserContext';
import { errorMessageFor } from './helpers';

import styles from './styles.module.scss';
import _ from 'lodash';

type PropsDef = {
  onDateChange: (date: Date) => void;
};

export default function ScheduleBlockForm(props: PropsDef) {
  const { t } = useTranslation();
  const user = useContext(UserContext);
  const { onDateChange } = props;

  const {
    control,
    register,
    watch,
    setValue,
    formState: { errors },
  } = useFormContext();

  const watchTimeZone = watch('timeZone');
  const watchStartTime = watch('start');
  const watchEndTime = watch('end');
  const watchCoaches = watch('coachProfiles');

  const luxonStartAt = useMemo(() => {
    return DateTime.fromISO(watchStartTime, { zone: watchTimeZone });
  }, [watchStartTime]);

  const luxonEndAt = useMemo(() => {
    return DateTime.fromISO(watchEndTime, { zone: watchTimeZone });
  }, [watchEndTime]);

  const allowPrivateBlock = watchCoaches?.length === 1 &&
                            watchCoaches[0].id === user.id &&
                            user.role === RoleType.COACH;

  const onStartTimeChange = (changeEvent: any) => {
    const newStartAt = DateTime.fromISO(changeEvent.target.value, { zone: watchTimeZone });
    const diff =  newStartAt.diff(luxonStartAt);

    setValue('start', changeEvent.target.value);
    if (luxonEndAt.isValid) {
      setValue('end', luxonEndAt.plus(diff));
    }
  };

  const onEndTimeChange = (changeEvent: any) => {
    const newEndAt = DateTime.fromISO(changeEvent.target.value, { zone: watchTimeZone });

    if (newEndAt > luxonStartAt) {
      setValue('end', changeEvent.target.value);
      return;
    }

    setValue('end', luxonStartAt.plus({ minute: 15 }).toISO());
  };

  const calculateHours = () => {
    const dateList = [];

    _.range(0, 24*60, 15).forEach((value) => {
      const hour = Math.floor(value / 60);

      const minute = value % 60;
      const ampm = hour >= 12 ? "PM" : "AM";

      let label;

      if(hour === 0) {
        label = `12:${minute.toString().padStart(2, "0")} ${ampm}`;
      } else if(hour > 12) {
        label = `${hour-12}:${minute.toString().padStart(2, "0")} ${ampm}`;
      } else {
        label = `${hour}:${minute.toString().padStart(2, "0")} ${ampm}`;
      }

      dateList.push({
        value,
        label,
      });
    });

    return dateList;
  };

  const dateList = calculateHours();

  return (
    <>
      {allowPrivateBlock && (
        <FormItem error={errorMessageFor(errors, 'private')} className={`${styles.input_field} ${styles.checkbox_field}`}>
          <Switch
            {...register('private')}
            checked={watch('private') || false}
            name="private"
            onChange={(checked) => {
              setValue('private', checked);
            }}
            offColor="#C2C8D8"
            onColor="#27A4FF"
            onHandleColor="#FFFFFF"
            handleDiameter={25}
            uncheckedIcon={false}
            checkedIcon={false}
            height={30}
            width={50}
          />

          <label>{t('Private')}</label>
        </FormItem>
      )}

      <FormItem error={errorMessageFor(errors, 'title')} className={styles.input_field}>
        <label>{t('Title')}</label>
        <input {...register('title')} placeholder={t('Event Name')} />
      </FormItem>

      <FormItem error={errorMessageFor(errors, 'start')} className={styles.input_field}>
        <label>{t('Date')}</label>
        <DateSelector {...register('date', { onChange: onDateChange })} />
      </FormItem>

      <div className={styles.form_row}>
        <FormItem error={errorMessageFor(errors, 'start')} className={styles.input_field}>
          <label>{t('Start Time')}</label>
          <TimeRangeInput
            {...register('start', { onChange: onStartTimeChange })}
            control={control}
            timeZone={watchTimeZone}
            dateList={dateList}
            customStartTime={null}
          />
        </FormItem>

        <FormItem error={errorMessageFor(errors, 'end')} className={styles.input_field}>
          <label>{t('End Time')}</label>
          <TimeRangeInput
            {...register('end', { onChange: onEndTimeChange })}
            control={control}
            timeZone={watchTimeZone}
            dateList={dateList}
            customStartTime={watchStartTime}
          />
        </FormItem>
      </div>

      <RecurrenceSelector />

      <FormItem error={errorMessageFor(errors, 'notes')} className={styles.input_field}>
        <label>{t('Notes')}</label>
        <textarea style={{ height: 100, resize: 'none' }} {...register('notes')} placeholder={t('Add note here')}/>
      </FormItem>
    </>
  );
}
